import { initContract } from '@ts-rest/core';
import { agentContract } from './agent';
import { kycContract } from './kyc';
import { addressLookupContract } from './address-lookup';
import { coacContract } from './coac';
import { coacInstructionContract } from './coac-instruction';
import { coacCalendarContract } from './coac-calendar';
import { coacControlsContract } from './coac-controls';
import { coacEdiContract } from './coac-edi';
import { reconciliationContract } from './reconciliation';
import { managerAliasContract } from './manager-alias';
export type {
    IngestDoor,
    IngestReasonCount,
    IngestRunSummary,
    LatestIngestRunsResponse,
    IngestRunExceptionRow,
    IngestRunExceptionsResponse,
} from './ingest-runs';
import { ingestRunsContract } from './ingest-runs';
export type {
    AutomaticFeedOutcome,
    AutomaticFeedToday,
    AutomaticFeedReceipt,
    AutomaticFeedDay,
    AutomaticFeedSource,
    AutomaticFeedsResponse,
} from './automatic-feeds';
export {
    automaticFeedOutcomeSchema,
    automaticFeedTodaySchema,
    automaticFeedReceiptSchema,
    automaticFeedDaySchema,
    automaticFeedSourceSchema,
    automaticFeedsResponseSchema,
} from './automatic-feeds';
import { automaticFeedsContract } from './automatic-feeds';
export type {
    IngestPlanKind,
    PlanChangeGroup,
    IngestPlanStatus,
    PlanSummaryLine,
    PlanFieldChange,
    PlanPreviewLine,
    PlanChangeOutcome,
    PlanChange,
    PlanGroupTally,
    PlanOwnerFacet,
    IngestPlan,
    IngestPlanView,
    IngestPlanChangesResponse,
    PlanChangeFilter,
    ApplyPlanResponse,
} from './ingest-plans';
export {
    planChangeGroupSchema,
    ingestPlanKindSchema,
    ingestPlanStatusSchema,
} from './ingest-plans';
import { ingestPlansContract } from './ingest-plans';
import { bookHealthContract } from './book-health';
import { clientBooksContract } from './client-books';
import { mcpAdminContract } from './mcp-admin';
import { retentionContract } from './retention';
import { firmConfigContract } from './firm-config';
import { systemStatusContract } from './system-status';
import { statusContract } from './status';
import { onboardingContract } from './onboarding';
import { consistencyContract } from './consistency';
import { factFindContract } from './fact-find';
import { suitabilityRefreshContract } from './suitability-refresh';
import { suitabilityFlagsContract } from './suitability-flags';
import { factFindClarificationContract } from './fact-find-clarification';
import { documentsContract } from './documents';
import { clientReviewContract } from './client-review';
import { clientBookContract } from './client-book';
import { contactsContract } from './contacts';
import { adminContactsContract } from './admin-contacts';
import { clientsContract } from './clients';
import { accountsContract } from './accounts';
import { suitabilityRecordsContract } from './suitability-records';
import { relationshipsContract } from './relationships';
import { portalContract } from './portal';
import { clientApplicationContract } from './client-application';
import { clientApplicationStaffContract } from './client-application-staff';
import { governanceContract } from './governance';
import { vulnerabilityContract } from './vulnerability';
import { pcfContract } from './pcf';
import { kpiContract } from './kpi';
import { kpiReportingContract } from './kpi-reporting';
import { peerBenchmarkingContract } from './peer-benchmarking';
import { cashContract } from './cash';
import { sharePointContract } from './sharepoint';
import { opsContract } from './ops';
import { reportingContract } from './reporting';
import { reviewQueueContract } from './review-queue';
import { complyDashboardContract } from './comply-dashboard';
import { complyRegCalendarContract } from './comply-reg-calendar';
import { complyBreachRegisterContract } from './comply-breach-register';
import { complySuitabilityContract } from './comply-suitability';
import { complyConsumerDutyContract } from './comply-consumer-duty';
import { complyFinCrimeKycContract } from './comply-fin-crime-kyc';
import { complyTcTrackerContract } from './comply-tc-tracker';
import { complyCmpReviewsContract } from './comply-cmp-reviews';
import { countryRiskContract } from './country-risk';
// SEP-253 request 5 - the Administration register of firm document versions.
import { firmDocumentVersionsContract } from './firm-document-versions';
// SEP-256 request 4 - the firm library of client literature, in the Ops vault.
import { firmDocumentsContract } from './firm-documents';
import { paDealingContract } from './pa-dealing';
import { paDealingReviewContract } from './pa-dealing-review';
import { transfersContract } from './transfers';
import { transfersOpsContract } from './transfers-ops';
import { complyFairValueContract } from './comply-fair-value';
import { complyCallIntelligenceContract } from './comply-call-intelligence';
import { navContract } from './nav';
import { priorityActionsContract } from './priority-actions';
import { deskPagesContract } from './desk-pages';
import { documentDraftsContract } from './document-drafts';
import { suitabilityAssessmentContract } from './suitability-assessment';
// SEP-243 sections 31, 32, 34 and 41 - the review round of the locked version.
import { finalApprovalContract } from './final-approval';
import { suitabilityReviewContract } from './suitability-review';
import { vulnerabilityAssessmentContract } from './vulnerability-assessment';
import { amlAssessmentContract } from './aml-assessment';
import { entityAmlAssessmentContract } from './entity-aml-assessment';
import { mandateLetterContract } from './mandate-letter';
import { deskDashboardContract } from './desk-dashboard';
import { bookHistoryContract } from './book-history';
import { viewPagesContract } from './view-pages';
import { viewClientIntelligenceContract } from './view-client-intelligence';
import { securityReferenceContract } from './security-reference';
import { userPrefsContract } from './user-prefs';
import { dashboardLayoutContract } from './dashboard-layout';
import { workflowsContract } from './workflows';
import { analyticsContract } from './analytics';
import { auditContract } from './audit';
import { scenarioContract } from './scenario';

const c = initContract();

export const apiContract = c.router({
    agent: agentContract,
    coac: coacContract,
    // SEP-349 - the custodian instruction and the calendar notes.
    coacInstruction: coacInstructionContract,
    coacCalendar: coacCalendarContract,
    coacControls: coacControlsContract,
    coacEdi: coacEdiContract,
    onboarding: onboardingContract,
    // SEP-243 section 39 - the consistency rules engine, R-01 to R-49.
    consistency: consistencyContract,
    factFind: factFindContract,
    suitabilityRefresh: suitabilityRefreshContract,
    // SEP-293 section 8 - the Suitability Inconsistency Flags.
    suitabilityFlags: suitabilityFlagsContract,
    factFindClarification: factFindClarificationContract,
    documents: documentsContract,
    clientReview: clientReviewContract,
    clientBook: clientBookContract,
    contacts: contactsContract,
    adminContacts: adminContactsContract,
    clients: clientsContract,
    accounts: accountsContract,
    suitabilityRecords: suitabilityRecordsContract,
    relationships: relationshipsContract,
    portal: portalContract,
    // SEP-263 slice 5 - the public client application surface.
    clientApplication: clientApplicationContract,
    // SEP-263 slice 8 - Review Client Application, the staff read of one
    // submitted application. It is a GET and nothing else: the snapshot is
    // append-only, so no route here could rewrite an answer.
    clientApplicationStaff: clientApplicationStaffContract,
    governance: governanceContract,
    vulnerability: vulnerabilityContract,
    pcf: pcfContract,
    reconciliation: reconciliationContract,
    kpi: kpiContract,
    kpiReporting: kpiReportingContract,
    peerBenchmarking: peerBenchmarkingContract,
    cash: cashContract,
    managerAlias: managerAliasContract,
    ingestRuns: ingestRunsContract,
    automaticFeeds: automaticFeedsContract,
    ingestPlans: ingestPlansContract,
    bookHealth: bookHealthContract,
    clientBooks: clientBooksContract,
    mcpAdmin: mcpAdminContract,
    retention: retentionContract,
    firmConfig: firmConfigContract,
    systemStatus: systemStatusContract,
    // Public, unauthenticated service health (web `/status`).
    status: statusContract,
    sharePoint: sharePointContract,
    ops: opsContract,
    reporting: reportingContract,
    reviewQueue: reviewQueueContract,
    complyDashboard: complyDashboardContract,
    complyRegCalendar: complyRegCalendarContract,
    complyBreachRegister: complyBreachRegisterContract,
    complySuitability: complySuitabilityContract,
    complyConsumerDuty: complyConsumerDutyContract,
    complyFinCrimeKyc: complyFinCrimeKycContract,
    complyTcTracker: complyTcTrackerContract,
    countryRisk: countryRiskContract,
    firmDocumentVersions: firmDocumentVersionsContract,
    firmDocuments: firmDocumentsContract,
    // SEP-264 PA Dealing: the employee side and the Compliance side.
    paDealing: paDealingContract,
    paDealingReview: paDealingReviewContract,
    // SEP-283 Transfers: the Desk side and the Operations side.
    transfers: transfersContract,
    transfersOps: transfersOpsContract,
    complyCmpReviews: complyCmpReviewsContract,
    complyFairValue: complyFairValueContract,
    complyCallIntelligence: complyCallIntelligenceContract,
    nav: navContract,
    priorityActions: priorityActionsContract,
    deskPages: deskPagesContract,
    documentDrafts: documentDraftsContract,
    suitabilityAssessment: suitabilityAssessmentContract,
    suitabilityReview: suitabilityReviewContract,
    // SEP-243 sections 36 to 40 - the final approval gate, the mandate,
    // DocuSign and the audit pack.
    finalApproval: finalApprovalContract,
    vulnerabilityAssessment: vulnerabilityAssessmentContract,
    amlAssessment: amlAssessmentContract,
    entityAmlAssessment: entityAmlAssessmentContract,
    mandateLetter: mandateLetterContract,
    deskDashboard: deskDashboardContract,
    bookHistory: bookHistoryContract,
    viewPages: viewPagesContract,
    viewClientIntelligence: viewClientIntelligenceContract,
    securityReference: securityReferenceContract,
    userPrefs: userPrefsContract,
    // SEP-296 - the saved layout of each dashboard for one user.
    dashboardLayout: dashboardLayoutContract,
    workflows: workflowsContract,
    analytics: analyticsContract,
    audit: auditContract,
    scenario: scenarioContract,
    integrations: c.router({
        kyc: kycContract,
    }),
    // SEP-243 section 9, decision D5 - the server-side postcode proxy. The
    // vendor key never reaches the browser.
    addressLookup: addressLookupContract,
});

export * from './scenario';
export * from './coac-instruction';
export * from './coac-calendar';
export * from './coac-controls';
export * from './coac-edi';

export {
    agentContract,
    kycContract,
    addressLookupContract,
    coacContract,
    onboardingContract,
    consistencyContract,
    factFindContract,
    suitabilityRefreshContract,
    suitabilityFlagsContract,
    factFindClarificationContract,
    documentsContract,
    clientReviewContract,
    clientBookContract,
    contactsContract,
    adminContactsContract,
    clientsContract,
    accountsContract,
    suitabilityRecordsContract,
    relationshipsContract,
    portalContract,
    clientApplicationContract,
    clientApplicationStaffContract,
    governanceContract,
    vulnerabilityContract,
    pcfContract,
    reconciliationContract,
    kpiContract,
    kpiReportingContract,
    peerBenchmarkingContract,
    cashContract,
    managerAliasContract,
    ingestRunsContract,
    automaticFeedsContract,
    ingestPlansContract,
    bookHealthContract,
    clientBooksContract,
    mcpAdminContract,
    firmConfigContract,
    systemStatusContract,
    statusContract,
    sharePointContract,
    opsContract,
    reportingContract,
    reviewQueueContract,
    complyDashboardContract,
    complyRegCalendarContract,
    complyBreachRegisterContract,
    complySuitabilityContract,
    complyConsumerDutyContract,
    complyFinCrimeKycContract,
    complyTcTrackerContract,
    complyCmpReviewsContract,
    countryRiskContract,
    firmDocumentVersionsContract,
    firmDocumentsContract,
    paDealingContract,
    paDealingReviewContract,
    transfersContract,
    transfersOpsContract,
    complyFairValueContract,
    complyCallIntelligenceContract,
    navContract,
    priorityActionsContract,
    deskPagesContract,
    documentDraftsContract,
    suitabilityAssessmentContract,
    suitabilityReviewContract,
    finalApprovalContract,
    vulnerabilityAssessmentContract,
    amlAssessmentContract,
    entityAmlAssessmentContract,
    mandateLetterContract,
    deskDashboardContract,
    bookHistoryContract,
    viewPagesContract,
    viewClientIntelligenceContract,
    securityReferenceContract,
    userPrefsContract,
    dashboardLayoutContract,
    workflowsContract,
    analyticsContract,
    auditContract,
};
export type {
    AuditPillar,
    AuditDepartment,
    AuditTrailQuery,
    AuditTrailRow,
    AuditTrailResponse,
    AuditEmployee,
    AuditEmployeesResponse,
} from './audit';
export {
    auditPillarSchema,
    auditDepartmentSchema,
    auditTrailQuerySchema,
    auditTrailRowSchema,
    auditTrailResponseSchema,
    auditEmployeeSchema,
    auditEmployeesResponseSchema,
} from './audit';
export type {
    AnalyticsEventKind,
    AnalyticsClientEventKind,
    AnalyticsClientEvent,
    AnalyticsIngestRequest,
    AnalyticsIngestResponse,
} from './analytics';
export {
    ANALYTICS_EVENT_KINDS,
    analyticsEventKindSchema,
    analyticsClientEventKindSchema,
    analyticsClientEventSchema,
    analyticsIngestRequestSchema,
    analyticsIngestResponseSchema,
} from './analytics';
export type {
    WorkflowSummary,
    ListWorkflowsResponse,
    GetWorkflowResponse,
    SaveWorkflowRequest,
    SaveWorkflowResponse,
    DeleteWorkflowResponse,
    RunWorkflowResponse,
    GetScheduleResponse,
    SetScheduleRequest,
    SetScheduleResponse,
    ClearScheduleResponse,
} from './workflows';
export type {
    SecurityRefStatusDto,
    SecurityReferenceDto,
    ResolveSecurityReferenceRequest,
    ResolveSecurityReferenceResponse,
} from './security-reference';
export type {
    ClientReviewStatus,
    ClientReviewOrigin,
    ReviewStepKey,
    StepDisposition,
    AnchorFreshness,
    RegisterFilter,
    ReviewStepState,
    ClientReview,
    RegisterRow,
    RegisterResponse,
    ClientHeader,
    ClientReviewDetail,
    ClientReviewDigest,
    OpenReviewRequest,
    DispositionStepRequest,
    CompleteReviewRequest,
    CancelReviewRequest,
} from './client-review';
export type {
    ClientSort,
    ClientGrouping,
    ClientComplianceStatus,
    ClientBookRow,
    ClientBookStats,
    ListClientsResponse,
    HouseholdGroup,
    ListHouseholdsResponse,
    TriTierAccount,
    TriTierSuitabilityRecord,
    TriTierPrimaryContact,
    TriTierClientRow,
    TriTierLinkStatus,
    ListTriTierResponse,
    ClientIdentity,
    ClientMandate,
    ClientPerformance,
    ClientFactFind,
    ClientReviewSummary,
    ClientHolding,
    ClientCash,
    Portfolio,
    AccountDetail,
    HouseholdMember,
    ClientHousehold,
    ClientKycAssessment,
    ClientVulnerabilitySummary,
    ClientKyc,
    ClientContactLink,
    ClientSuitabilityRecordLink,
    ClientDetail,
    ExternalIdentifierDto,
    ClientExternalIdentifiersResponse,
    MatchCandidate,
    MatchCandidatesResponse,
    CreateClientRequest,
    UpdateClientRequest,
    RegisterPortfolioRequest,
    CreateHouseholdRequest,
    AddClientToHouseholdRequest,
    AddRelatedPersonRequest,
    ConfirmMatchRequest,
} from './client-book';
export type {
    ContactStatus,
    ContactKind,
    OrganisationType,
    ContactLifecycleAction,
    ContactRow,
    ContactsStats,
    ListContactsResponse,
    PersonContactDetail,
    OrganisationContactDetail,
    ContactDetail,
    ContactDuplicateCandidateDto,
    DuplicateCandidatesResponse,
    CreateContactRequest,
    UpdateContactRequest,
    SetContactStatusRequest,
    ReassignContactOwnerRequest,
    ContactCoOwner,
    ContactCoOwnerRequest,
    AssignContactOwnersBulkRequest,
    AssignContactOwnersSkipped,
    AssignContactOwnersBulkResponse,
    CreateOrganisationContactRequest,
    UpdateOrganisationContactRequest,
    MergeContactsRequest,
    MergeContactsResponse,
} from './contacts';
export type { ContactDeleteRefusal } from './contacts';
export { contactDeleteRefusalSchema } from './contacts';
export type { DeletedContactRow, ListDeletedContactsResponse } from './admin-contacts';
export { deletedContactRowSchema, listDeletedContactsResponseSchema } from './admin-contacts';
export {
    contactCoOwnerSchema,
    contactStatusSchema,
    contactKindSchema,
    organisationTypeSchema,
    contactLifecycleActionSchema,
    contactRowSearchFields,
    contactPickerLabel,
    CONTACT_LABEL_SEPARATOR,
    CONTACT_STATUS_LABELS,
} from './contacts';
export type {
    HolderResolutionStatus,
    AccountStatus,
    AccountRow,
    AccountsStats,
    ListAccountsResponse,
} from './accounts';
export type { ClientSearchRow, ClientSearchResponse } from './clients';
export { clientSearchRowSchema } from './clients';
export type {
    SuitabilityRecordStatus,
    SuitabilityRecordAssessmentType,
    SuitabilityRecordEntityTypeValue,
    EntityIdentityConditionalField,
    SuitabilityRecordLifecycleAction,
    SuitabilityRecordApprovalAction,
    SuitabilityRecordRow,
    SuitabilityRecordPrimaryContact,
    SuitabilityRecordEndpointType,
    SuitabilityRecordInvolvedParty,
    SuitabilityRecordLabelParts,
    SuitabilityRecordAnchorParts,
    SuitabilityRecordsStats,
    ListSuitabilityRecordsResponse,
    SuitabilityAssessmentContent,
    SuitabilityRecordDetail,
    CreateSuitabilityRecordRequest,
    UpdateSuitabilityRecordRequest,
    SetSuitabilityRecordStatusRequest,
    SetSuitabilityRecordApprovalRequest,
    ReassignSuitabilityRecordOwnerRequest,
    // SEP-178 — create the SUR's satellite artefacts in place.
    SuitabilityRecordClientCandidate,
    SuitabilityRecordClientCandidatesResponse,
    CreateSuitabilityRecordArtefactRequest,
    CreateSuitabilityRecordWithFactFindRequest,
} from './suitability-records';
export {
    suitabilityRecordStatusSchema,
    suitabilityRecordAssessmentTypeSchema,
    suitabilityRecordEntityTypeSchema,
    suitabilityRecordLifecycleActionSchema,
    suitabilityRecordApprovalActionSchema,
    suitabilityRecordPrimaryContactSchema,
    suitabilityRecordEndpointTypeSchema,
    suitabilityRecordInvolvedPartySchema,
    suitabilityRecordRowSearchFields,
    suitabilityRecordMatchesSearch,
    suitabilityRecordAnchor,
    suitabilityRecordLabelParts,
    suitabilityRecordPickerLabel,
    SUITABILITY_RECORD_LABEL_SEPARATOR,
    SUITABILITY_RECORD_LABEL_MAX_REFS,
    // Entity identity (SEP-186) — the conditional-field rule, mirrored from
    // the ontology so the form renders exactly what the Action accepts.
    ENTITY_IDENTITY_CONDITIONAL_KEYS,
    ENTITY_IDENTITY_FIELD_LABELS,
    ENTITY_TYPE_CONDITIONAL_FIELDS,
    entityIdentityConditionalFields,
} from './suitability-records';
export type {
    RelationshipEndpointType,
    RelationshipAppliesTo,
    RelationshipAgeBand,
    RelationshipRoleDto,
    ListRelationshipRolesResponse,
    RelationshipRow,
    ListRelationshipsResponse,
    RelationshipVia,
    RelationshipEndpoint,
    BookRelationshipRow,
    BookRelationshipsStats,
    ListBookRelationshipsResponse,
    CreateRelationshipRequest,
    RelationshipMutationResponse,
    UpdateRelationshipRequest,
    CreateRelationshipRoleRequest,
    UpdateRelationshipRoleRequest,
    ClientLinkReplacementMode,
    ClientLinkReplacement,
    CompleteClientLinksRequest,
    ClientLinkStep,
    ClientLinkOutcome,
    CompleteClientLinksResponse,
} from './relationships';
export {
    relationshipEndpointTypeSchema,
    relationshipAppliesToSchema,
    relationshipAgeBandSchema,
    relationshipPairKind,
    relationshipViaSchema,
    derivedRelationshipId,
    relationshipEndpointSchema,
    bookRelationshipRowSchema,
    listBookRelationshipsResponseSchema,
    isSingleRoleRelationshipKind,
    RELATIONSHIP_ENDPOINT_TYPE_LABELS,
    completeClientLinksRequestSchema,
    completeClientLinksResponseSchema,
} from './relationships';
export type {
    DeskDashboardStats,
    DeskDashboardClientRow,
    DeskDashboardResponse,
    DeskPerformanceRange,
    DeskBenchmarkRange,
    DeskBookReturnSource,
    DeskDashboardOverview,
    DeskDashboardHolding,
    DeskDashboardMeeting,
    DeskDashboardFactFindRenewal,
    DeskDashboardVulnerabilityReview,
    DeskDashboardActionPoint,
    DeskDashboardBreach,
} from './desk-dashboard';
export type {
    ComplyDashboardKpis,
    ComplyDashboardDeadline,
    ComplyAlertFeedItem,
    ComplyAttentionItem,
    ComplyDashboardResponse,
} from './comply-dashboard';
export type {
    ArtefactSummaryRow,
    CallArtefactRow,
    CounterpartySuggestion,
    RelationshipProposalRow,
    MonthlyNarrativeRow,
    ReviewQueuePendingResponse,
    FrontendReviewCounts,
    ReviewQueueCountsResponse,
    ContextField,
    ContextTranscriptLine,
    ContextBlock,
    ReviewContextResponse,
    ReviewContextQuery,
} from './review-queue';
export {
    contextFieldSchema,
    contextTranscriptLineSchema,
    contextBlockSchema,
    reviewContextResponseSchema,
    reviewContextQuerySchema,
} from './review-queue';
export type {
    RegulatoryReturnDto,
    RegulatoryReturnStatusDto,
    RegCalendarResponse,
} from './comply-reg-calendar';
export { regulatoryReturnStatusSchema } from './comply-reg-calendar';
export type { CountryRiskRegisterResponse, SetCountryRiskClassificationBody } from './country-risk';
// The SEP-228 AML workflow wire types.
export type {
    AmlQuestionnaireDerivedDto,
    AmlQuestionnaireResultDto,
    AmlEddStatusDto,
    AmlDecisionDto,
} from './aml-assessment';
export {
    amlQuestionnaireDerivedSchema,
    amlQuestionnaireResultSchema,
    amlEddStatusSchema,
    amlDecisionSchema,
} from './aml-assessment';
export {
    countryRiskRegisterResponseSchema,
    setCountryRiskClassificationBodySchema,
} from './country-risk';
export {
    firmDocumentVersionRowSchema,
    firmDocumentVersionsResponseSchema,
    setFirmDocumentVersionBodySchema,
} from './firm-document-versions';
export type {
    FirmDocumentVersionRow,
    FirmDocumentVersionsResponse,
    SetFirmDocumentVersionBody,
} from './firm-document-versions';
export { firmDocumentSchema, firmDocumentsResponseSchema } from './firm-documents';
export type { FirmDocumentDto, FirmDocumentsResponse } from './firm-documents';
export type {
    BreachCategoryDto,
    BreachSeverityDto,
    BreachRegisterStatusDto,
    BreachRegisterEntryDto,
    BreachRegisterResponse,
} from './comply-breach-register';
export {
    breachCategorySchema,
    breachSeveritySchema,
    breachRegisterStatusSchema,
} from './comply-breach-register';
export type {
    SuitabilityAlertDto,
    SuitabilityAlertTypeDto,
    SuitabilityAlertStatusDto,
    SuitabilityAlertSeverityDto,
    VigilFindingDto,
    SuitabilityResponse,
} from './comply-suitability';
export {
    suitabilityAlertTypeDtoSchema,
    suitabilityAlertStatusDtoSchema,
    suitabilityAlertSeverityDtoSchema,
} from './comply-suitability';
export type {
    ConsumerDutyResponse,
    ConsumerDutyAssessmentDto,
    ConsumerDutyClientScoreDto,
    VulnerableCustomerDto,
} from './comply-consumer-duty';
export type {
    FinCrimeKycResponse,
    EddOutcomeDto,
    KycReviewDto,
    EddCaseDto,
} from './comply-fin-crime-kyc';
export { eddOutcomeSchema } from './comply-fin-crime-kyc';
export type {
    TcTrackerResponse,
    TcRecordDto,
    CpdEntryDto,
    RecordCpdEntryBody,
} from './comply-tc-tracker';
export type {
    CmpReviewsResponse,
    CmpReviewItemDto,
    CmpFindingDto,
    AddCmpFindingBody,
} from './comply-cmp-reviews';
export type { FairValueResponse } from './comply-fair-value';
export type {
    CallIntelligenceResponse,
    CallExtractedFactDto,
    CallActionItemDto,
    CallKbNoteDto,
    CallIntelligenceDeskCallDto,
    CallIntelligenceClientDto,
} from './comply-call-intelligence';
export type {
    DeskClientKnowledgeResponse,
    DeskClientKB,
    DeskKBNote,
    DeskKBGraphNode,
    DeskKBGraphEdge,
    RelationshipProposalDto,
    ClientKnowledgeDocumentDto,
    KnowledgeAssignmentRow,
    RecordKnowledgeItem,
    RecordKnowledgeResponse,
    DeskCallsMeetingsResponse,
    DeskCallRow,
    DeskCallUploadRow,
    DeskCallUploadStatus,
    DeskCallUploadCallType,
    AssignCallUploadRequest,
    CallUploadContactOption,
    ListCallUploadContactsResponse,
    UnassignedCallCandidate,
    DeskUnassignedCallRow,
    ListUnassignedCallsResponse,
    AssignUnassignedCallRequest,
    SetCallFirmSpeakerRequest,
    DeskCallFactRow,
    DeskCallActionRow,
    DeskCallNoteRow,
    CallCitationSource,
    CallCitationSourcesResponse,
    CallTranscriptMatch,
    SearchCallTranscriptsResponse,
    OpsAuditLogEntryDto,
    OpsAuditLogResponse,
    MandateCatalogueEntryDto,
    MandateCatalogueResponse,
    DeskPortfolioHoldingDto,
    DeskPortfolioHoldingsResponse,
    DeskPortfolioHolderAccount,
    DeskPortfolioHolder,
    DeskPortfolioHoldingHoldersResponse,
    FeedItemDto,
    FeedItemActionOk,
} from './desk-pages';
// The upload route takes the same three fields as optional multipart fields
// (SEP-279), so the server parses them with this schema.
export { assignCallUploadRequestSchema, CALL_TRANSCRIPT_SEARCH_MIN_LENGTH } from './desk-pages';
export { navCountsResponseSchema, navSeenKeySchema, navSeenRequestSchema } from './nav';
export type { NavCountsResponse, NavSeenKey, NavSeenRequest } from './nav';
export type {
    SuitabilityAssessmentFields,
    SuitabilityAssessmentDto,
    // SEP-243 sections 30, 33 and 35 - one immutable snapshot of the review.
    SuitabilityAssessmentVersionDto,
    SuitabilityAssessmentGetResponse,
    SuitabilityPrefillSuggestion,
    SuitabilityPrefillReviewStatus,
    // SEP-243 slice 9 - the read-only pull-through block of the Internal Review.
    SuitabilityPullThroughDto,
    SuitabilityKnownFactDto,
    FactFindSourceDto,
    FactFindRiskSummaryDto,
    // SEP-293 - the measures A to M of the ISA v3 form.
    InternalSuitabilityMeasuresDto,
} from './suitability-assessment';
export {
    suitabilityAssessmentFieldsSchema,
    suitabilityAssessmentDtoSchema,
    // SEP-243 sections 30, 33 and 35.
    suitabilityAssessmentVersionSchema,
    suitabilityAssessmentGetResponseSchema,
    suitabilityPrefillSuggestionSchema,
    suitabilityPrefillReviewStatusSchema,
    suitabilityPrefillListResponseSchema,
    suitabilityPrefillActionResponseSchema,
    // SEP-243 slice 9.
    suitabilityPullThroughSchema,
    suitabilityKnownFactSchema,
    factFindSourceSchema,
    factFindSourceKindSchema,
    factFindRiskSummarySchema,
    clientStorySummarySchema,
    // SEP-293 - the ISA v3 form.
    internalSuitabilityMeasuresSchema,
    regularWithdrawalSchema,
    oneOffWithdrawalSchema,
} from './suitability-assessment';
// SEP-243 sections 31, 32, 34 and 41 - the peer review, the Compliance review
// and the two work queues.
export type {
    PeerReviewAssignmentStatus,
    PeerReviewAssignmentDto,
    CompletedPeerReviewDto,
    PeerReviewWorkspace,
    PeerReviewFlag,
    PeerReviewFlagChoice,
    PeerVulnerabilitySummary,
    SuitabilityPeerReviewDecision,
    SuitabilityPeerReviewDto,
    SuitabilityComplianceReviewDecision,
    SuitabilityComplianceReviewDto,
    ComplianceDecidedReviewDto,
    ComplianceReviewQueueRow,
    ComplianceReviewQueuesResponse,
    SuitabilityReviewDossier,
} from './suitability-review';
export {
    peerReviewAssignmentStatusSchema,
    peerReviewAssignmentSchema,
    peerReviewQueueResponseSchema,
    completedPeerReviewSchema,
    completedPeerReviewsResponseSchema,
    complianceDecidedReviewSchema,
    complianceDecidedReviewsResponseSchema,
    peerReviewWorkspaceSchema,
    peerReviewFlagSchema,
    peerReviewFlagChoiceSchema,
    peerVulnerabilitySummarySchema,
    suitabilityPeerReviewDecisionSchema,
    suitabilityPeerReviewSchema,
    suitabilityComplianceReviewDecisionSchema,
    suitabilityComplianceReviewSchema,
    complianceReviewQueueRowSchema,
    complianceReviewQueuesResponseSchema,
    suitabilityReviewDossierSchema,
} from './suitability-review';
export type {
    VulnerabilityAnswersDto,
    VulnerabilityAssessmentDto,
    VulnerabilityAssessmentGetResponse,
    VulnerabilityOversightSummary,
    ContactVulnerabilitySummary,
    ListContactVulnerabilityAssessmentsResponse,
} from './vulnerability-assessment';
export {
    vulnerabilityAnswersSchema,
    vulnerabilityAnswersInstrumentSchema,
    vulnerabilityAssessmentDerivedSchema,
    vulnerabilityAssessmentDtoSchema,
    vulnerabilityDriverKeySchema,
    vulnerabilityDriverOutcomeSchema,
    vulnerabilityDriverResultSchema,
    vulnerabilityAssessmentGetResponseSchema,
    vulnerabilityBandCountsSchema,
    vulnerabilityDriverCountsSchema,
    vulnerabilityOversightSummarySchema,
    contactVulnerabilitySummarySchema,
    listContactVulnerabilityAssessmentsResponseSchema,
} from './vulnerability-assessment';
export type {
    AmlAnswersDto,
    AmlAssessmentDto,
    ContactAmlSummary,
    ListContactAmlAssessmentsResponse,
} from './aml-assessment';
export {
    amlAnswersSchema,
    amlAssessmentDerivedSchema,
    amlAssessmentDtoSchema,
    contactAmlSummarySchema,
    listContactAmlAssessmentsResponseSchema,
} from './aml-assessment';
// Entity AML (SEP-186) — the EAML-#### SUR-level instrument for entity clients.
export type {
    EntityAmlAnswersDto,
    EntityAmlAssessmentDto,
    EntityAmlSummary,
    ListEntityAmlAssessmentsResponse,
} from './entity-aml-assessment';
export {
    entityAmlAnswersSchema,
    entityAmlAssessmentDtoSchema,
    entityAmlSummarySchema,
    listEntityAmlAssessmentsResponseSchema,
} from './entity-aml-assessment';
export type {
    MandateLetterInputs,
    MandateLetterContext,
    MandateLetterDto,
    MandateLetterGetResponse,
} from './mandate-letter';
export {
    mandateLetterInputsSchema,
    mandateLetterContextSchema,
    mandateLetterDtoSchema,
    mandateLetterGetResponseSchema,
} from './mandate-letter';
export type {
    ReviewableKind,
    ComposerAgent,
    StaffActionOk,
    StaffActionError,
    StaffActionResult,
    AgentTaskListQuery,
    ScheduledRun,
    ListScheduledRunsResponse,
    ReviewDecisionVerb,
    ReviewDecision,
    ArtefactReviewDecision,
    TaskReviewDecision,
    ReviewDecisionResult,
    ReviewDecisionsResponse,
    KnowledgeAssignmentTarget,
    KnowledgeAssignmentTargetType,
    KnowledgeItemType,
    RelationshipCounterparty,
    RelationshipPromotion,
} from './agent';
export {
    reviewableKindSchema,
    composerAgentSchema,
    staffActionOkSchema,
    staffActionErrorSchema,
    agentTaskListQuerySchema,
    scheduledRunSchema,
    listScheduledRunsResponseSchema,
    reviewDecisionVerbSchema,
    reviewDecisionSchema,
    reviewDecisionsRequestSchema,
    reviewDecisionsResponseSchema,
    knowledgeAssignmentTargetSchema,
    knowledgeAssignmentTargetTypeSchema,
    knowledgeItemTypeSchema,
    relationshipCounterpartySchema,
    relationshipPromotionSchema,
} from './agent';
export type {
    FirmOverviewResponse,
    FirmOverviewBreach,
    FirmOverviewKycReview,
    FirmOverviewComplianceAlert,
    ViewAggregatesResponse,
    // Operational Intelligence signal DTOs (View > Operational Intelligence).
    OperationalIntelligenceResponse,
    OpsReconSignal,
    OpsOverdrawnSignal,
    OpsTradeSignal,
    OpsLabelCount,
    // Custodian Analytics feed-coverage DTOs (View > Custodian Analytics).
    CustodianAnalyticsResponse,
    CustodianCoverageRow,
    // View performance DTOs (issue 352 Workstream A).
    FinancialPerformanceResponse,
    FinancialPerformanceGroup,
    InvestmentPerformanceResponse,
    AccountReturnsRow,
    CategoryReturn,
    FundManagementListResponse,
    TrustDetailResponse,
    RnsAnnouncementDto,
    ItpBreachDto,
    // Fund-management row DTOs (the per-trust screens read these directly).
    InvestmentTrustRow,
    ExposureSnapshotRow,
    DiscountSnapshotRow,
    GearingSnapshotRow,
    PolicyClauseRow,
    ShareholderRow,
    ContinuationVoteRow,
    BrokerCoverageRow,
    FundReportDraftRow,
} from './view-pages';
export type {
    AttritionBand,
    AttritionRow,
    AttritionBandCounts,
    AttritionKpiSummary,
    ClientIntelligenceResponse,
} from './view-client-intelligence';
export { attritionBandSchema } from './view-client-intelligence';
export type {
    DocumentTaxonomy,
    VaultRow,
    SharePointStatus,
    ExportPdfRequest,
    ExportPdfResponse,
} from './sharepoint';
export type {
    KycVendor,
    KycMatch,
    KycScreenStatus,
    KycScreenResult,
    KycApplicant,
    KycScreenIndividualRequest,
    KycScreenEntityRequest,
    KycApplicantRequest,
    KycWebhookPayload,
    KycStatusResponse,
    KycVendorStatus,
    KycCapability,
    KycCapabilityStatus,
    // SEP-228 — the SmartSearch identity verification door.
    SmartsearchIdentityOutcome,
    SmartsearchVerificationStatus,
    SmartsearchVerifyPerson,
    SmartsearchVerifyRequest,
    SmartsearchVerificationOutcome,
    SmartsearchVerifyResponse,
    SmartsearchOngoingAlertRequest,
    SmartsearchOngoingAlertResponse,
    // SEP-228 — the Compliance identity review doors (§6, §7).
    IdentityVerificationDto,
    ListIdentityVerificationsResponse,
    IdentityVerifyRequest,
    IdentityRequestEvidenceRequest,
    IdentityRefuseRequest,
    IdentityDecisionResponse,
    // SEP-228 — the temporary manual identity door (§5).
    ExternalIdentityPerson,
    ExternalIdentityOutcome,
    RecordExternalIdentityCheckRequest,
    RecordExternalIdentityCheckResponse,
} from './kyc';
export type {
    CoacActionRow,
    CoacSupportingLink,
    CoacActionListResponse,
    CoacElectionStatus,
    CoacOpsStatus,
    CoacRoutingOutcome,
    CoacUrgencyRating,
    CoacDeadlineBasis,
    CustodianResponseDeadline,
    InternalResponseDeadline,
    CoacRouteToImRequest,
    CoacRecordDecisionRequest,
    CoacApproveElectionRequest,
    CoacDefaultElectionRequest,
    // Exposure (command centre — discretionary / advisory / execution-only split)
    CoacServiceType,
    CoacExposureBucket,
    CoacExposureSummary,
    CoacExposureSummaryRequest,
    CoacExposureLine,
    CoacExposureBreakdown,
    // Per-account elections (WP-E)
    CoacAccountElectionRow,
    CoacAccountElectionView,
    CoacRecordAccountElectionRequest,
    CoacImResponse,
    CoacResponsesBreakdown,
    CoacResponsesRow,
    CoacResponsesSummaryRow,
    // The affected-portfolio tree (SEP-251)
    CoacPortfolioResponseStatus,
    CoacAffectedPortfolio,
    CoacAffectedClient,
    CoacAffectedImGroup,
    CoacAffectedPortfolios,
    // SEP-276 - Operations corrects the affected set
    CoacImPortfolio,
    CoacAddPortfoliosRequest,
    CoacRemovePortfolioRequest,
    CoacSetPortfolioQuantityRequest,
    CoacCompleteActionRequest,
    CoacBreakdownExplanation,
    CoacRecallResponse,
    // ADR-0020 action draft types
    CoacActionDraftRow,
    CoacNoticeDate,
} from './coac';
export type {
    OnboardingPipelineStage,
    OnboardingPipelineCaseStatus,
    OnboardingEnquirySource,
    KycResultStatus,
    PostMeetingNote,
    KycResult,
    SourceOfFundsDeclaration,
    OnboardingAuditEntry,
    OnboardingPipelineCase,
    OnboardingListResponse,
    OnboardingCaseResponse,
    CreateEnquiryRequest,
    UpdateContactDetailsRequest,
    AssignImRequest,
    TransitionRequest,
    PostMeetingNoteRequest,
    RemindRequest,
    ActorRequest,
    OnboardingCallKnowledgeResponse,
    OnboardingStageMetric,
    OnboardingAnalyticsResponse,
    // The AML and KYC track, and the compliance referral (SEP-243 sections 14
    // to 16).
    AmlTrackStatus,
    AmlReferralFlag,
    AmlReferralNote,
    AmlReferralOutcome,
    AmlCaseReferral,
    RunAmlVendorRequest,
    RunAmlVendorResponse,
    AmlReferralListResponse,
    AmlReferralResponse,
    RecordAmlReferralNoteRequest,
    ResolveAmlReferralRequest,
    // SEP-243 slice 7 - the two document registers, the classification and HNW
    // record, the structured conflicts, and the Compliance copy seam
    // (sections 4, 5, 10, 11 and decision D4).
    ConflictComplianceStatus,
    OnboardingConflictRecord,
    ClientClassification,
    ClassificationAssessmentStatus,
    ApprovedAssessmentClassification,
    ClientClassificationRecord,
    OnboardingRegistersResponse,
    OpenProvidedDocumentRequest,
    RecordProvidedDocumentRequest,
    // SEP-253 - one act of delivery, and the version correction beside it.
    RecordDocumentSendRequest,
    CorrectProvidedDocumentVersionRequest,
    OpenReceivedDocumentRequest,
    RecordReceivedDocumentRequest,
    CheckReceivedDocumentRequest,
    RecordConflictRequest,
    ReviewConflictRequest,
    CompleteConflictDisclosureRequest,
    RecordClassificationRequest,
    AssessClassificationRequest,
    PublishComplianceCopyRequest,
    ComplianceCopyResponse,
    // SEP-263 decision D11 - the origin of a client-led application, and the
    // possible duplicates the route leaves for the investment manager.
    StaffEnquirySource,
    OnboardingPossibleDuplicate,
    // SEP-289 - the application form stage.
    ApplicationFormStatus,
    InviteApplicationFormRequest,
    InviteApplicationFormResponse,
    RecordApplicationFormReceivedOutsideRequest,
    // SEP-337 - the accounts that Operations opened for a case.
    OpenedAccountDto,
    OpenedAccountListResponse,
    RecordOpenedAccountRequest,
    CorrectOpenedAccountRequest,
} from './onboarding';
// SEP-289. The application form stage: its status values and the two requests.
export {
    applicationFormStatusSchema,
    inviteApplicationFormRequestSchema,
    recordApplicationFormReceivedOutsideRequestSchema,
} from './onboarding';
// SEP-263 decision D11. The origin value the client-led route stamps, the five
// origins the staff dialog offers, and the schemas beside them.
export {
    ONLINE_CLIENT_APPLICATION_SOURCE,
    STAFF_ENQUIRY_SOURCES,
    staffEnquirySourceSchema,
    onboardingPossibleDuplicateSchema,
} from './onboarding';
// The stored post-meeting note. `post_meeting_note` is a TEXT column that holds
// a JSON document, so the server seam parses the string and then checks it
// against this schema.
export { postMeetingNoteSchema } from './onboarding';
// The closed value sets of SEP-243 slice 7. The browser reads them, because the
// register forms offer the options.
export {
    conflictComplianceStatusSchema,
    clientClassificationSchema,
    classificationAssessmentStatusSchema,
    onboardingConflictRecordSchema,
    clientClassificationRecordSchema,
    // SEP-253 - the request shapes of the send and of the version correction.
    recordDocumentSendRequestSchema,
    correctProvidedDocumentVersionRequestSchema,
} from './onboarding';
// The track positions from which Ops may run the vendor. The browser reads it,
// because a button that the Action refuses must not look pressable.
export { AML_VENDOR_RUN_FROM, MANUAL_AML_SOURCE, amlManualResultSchema } from './onboarding';
export type { AmlManualResult } from './onboarding';
// The closed value sets of the evidenced amendment (SEP-243 sections 7 and 42).
// The browser reads them, because the record form offers the source kinds.
export {
    factFindAmendmentSourceKindSchema,
    factFindAmendmentStatusSchema,
    factFindAmendmentSchema,
    confirmedFactFindAnswerSchema,
    // SEP-261 - the joint fact find. The browser reads the account type,
    // because the create dialog offers Sole and Joint.
    factFindRecordAccountSchema,
    factFindRecordContactSchema,
    factFindRecordLatestSchema,
    factFindRecordOptionSchema,
    factFindRecordOptionsResponseSchema,
    factFindAccountGroupOptionSchema,
    factFindAccountGroupOptionsResponseSchema,
    factFindSiblingSchema,
    factFindResponseSourceSchema,
    factFindSeededSourceSchema,
    contactFactFindSchema,
    contactFactFindsResponseSchema,
    // SEP-288 - the SUI list.
    factFindFormVersionSchema,
    factFindFormLayoutSchema,
    factFindFormAudienceSchema,
    factFindAudienceOf,
    isForFactFindAudience,
    suiOutcomeSchema,
    suiComplianceStatusSchema,
    suiSummarySchema,
    suiListResponseSchema,
} from './fact-find';
export type {
    FactFindStatus,
    FactFindFreshness,
    FactFindClientType,
    CflBand,
    PrefillReviewStatus,
    VulnerabilityDriver,
    FactFindAnswer,
    FactFindResponse,
    FactFindResponseSource,
    FactFindSeededSource,
    AtrAssessment,
    CflWorkingStep,
    CflCalculation,
    FactFindVulnerabilityMarker,
    FactFindAnnotation,
    FactFindAuditEntry,
    FactFindVersion,
    FactFind,
    FactFindRegisterRow,
    FactFindRiskSummaryRow,
    FactFindRiskSummaryResponse,
    PrefillSuggestion,
    FactFindRegisterResponse,
    CreateDraftRequest,
    FactFindClientOption,
    FactFindClientsResponse,
    CreateFactFindRequest,
    CreateFactFindResponse,
    FactFindTransitionRequest,
    AddAnnotationRequest,
    FactFindReceiptDoor,
    LockReceiptRequest,
    FactFindAmendmentDto,
    ConfirmedFactFindAnswerDto,
    FactFindAmendmentsResponse,
    RecordAmendmentRequest,
    WithdrawAmendmentRequest,
    RefreshRequest,
    RefreshResponse,
    PrefillListResponse,
    PrefillActionResponse,
    FactFindSigningStatus,
    FactFindDocumentRecipient,
    FactFindDocument,
    FactFindDocumentResponse,
    FactFindSigningState,
    FactFindSigningStateResponse,
    SendForSignatureSigner,
    SendForSignatureRequest,
    MarkSignedRequest,
    // SEP-261 - the joint fact find.
    FactFindRecordAccount,
    FactFindRecordContact,
    FactFindRecordLatest,
    FactFindRecordOption,
    FactFindRecordOptionsResponse,
    FactFindAccountGroupOption,
    FactFindAccountGroupOptionsResponse,
    FactFindSibling,
    ContactFactFind,
    ContactFactFindsResponse,
    // SEP-288 - the SUI list.
    FactFindFormVersion,
    FactFindFormLayout,
    FactFindFormAudience,
    FactFindSourceRecord,
    FactFindSourceSection,
    SuiOutcome,
    SuiComplianceStatus,
    SuiSummary,
    SuiListResponse,
    FactFindSuggestion,
    FactFindRecordChange,
    FactFindSuggestionsResponse,
    CreateNewFactFindRequest,
    CreateNewFactFindResponse,
    SuiHistoryEntry,
    SuiHistoryResponse,
} from './fact-find';
export type { DocumentArea, DocumentStatus, DocumentDto } from './documents';
export { documentAreaSchema, documentStatusSchema, documentSchema } from './documents';
export type {
    PortalSessionStatus,
    PortalMessageRole,
    PortalDeliveryChannel,
    PortalDocumentCategory,
    PortalNotificationKind,
    PortalNotificationStatus,
    PortalCitation,
    ClientPortalSession,
    ClientPortalMessage,
    PortalLibraryDocument,
    ClientDocumentAccess,
    ClientPortalNotification,
    PortalHolding,
    PortalDashboard,
    PortalApplicationStatus,
    PortalOverdrawnAccount,
    PortalActionItem,
    PortalDocumentRef,
    PortalPortfolio,
    PortalCashAccount,
    PortalCashByCurrency,
    PortalCash,
    PortalExposureBand,
    PortalConcentrationLine,
    PortalExposure,
    PortalHoldingDetail,
    PortalHoldingDetailResponse,
    PortalProfile,
    LoginRequest,
    LoginResponse,
    PortalCredential,
    PortalCredentialIssue,
    PortalCredentialRegenerate,
    EnsureCredentialRequest,
    RegenerateCredentialRequest,
    PortalSessionResponse,
    PostMessageRequest,
    PortalMessagesResponse,
    PortalDocumentsResponse,
    PortalDocumentResponse,
    DocumentDownloadResponse,
    PortalNotificationsResponse,
    UpdateProfileRequest,
} from './portal';
// SEP-263 slice 5 - the public client application (the applicant account and
// the start page).
export type {
    ApplyInvestmentManager,
    ApplyInvestmentManagersResponse,
    ApplySignInRequest,
    ApplyStartRequest,
    ApplyInvitationResponse,
    ApplyAcceptInvitationRequest,
    ClientApplicationServiceType,
    ClientApplicationServiceTypeOption,
    ClientApplicationSessionResponse,
    ClientApplicationStatus,
    ClientApplicationSummary,
    // SEP-263 slice 6 - the draft of the journey.
    ClientApplicationDraft,
    ApplySaveDraftRequest,
    // SEP-263 slice 7 - the identity check and the one atomic submission.
    ClientApplicationVerification,
    ClientApplicationVerificationStatus,
    ClientApplicationSubmitResponse,
    // SEP-293 section 9 - the documents of the pack, before the submission.
    ApplicationDocument,
    ApplicationDocumentStatus,
    ApplicationDocumentsResponse,
} from './client-application';
export {
    CLIENT_APPLICATION_SERVICE_TYPE_OPTIONS,
    applyInvestmentManagerSchema,
    applyInvestmentManagersResponseSchema,
    applyPasswordSchema,
    applySignInRequestSchema,
    applyStartRequestSchema,
    applyInvitationResponseSchema,
    applyAcceptInvitationRequestSchema,
    clientApplicationServiceTypeSchema,
    clientApplicationSessionResponseSchema,
    clientApplicationStatusSchema,
    clientApplicationSummarySchema,
    clientApplicationAnswerSchema,
    clientApplicationAnswersSchema,
    clientApplicationFieldErrorSchema,
    clientApplicationSectionProgressSchema,
    clientApplicationDraftSchema,
    applySaveDraftRequestSchema,
    applyDraftRefusalSchema,
    // SEP-263 slice 7 - the identity check and the one atomic submission.
    clientApplicationVerificationStatusSchema,
    clientApplicationVerificationSchema,
    clientApplicationSubmitResponseSchema,
    clientApplicationSubmitRefusalSchema,
    applicationDocumentSchema,
    applicationDocumentStatusSchema,
    applicationDocumentsResponseSchema,
    acknowledgeApplicationDocumentRequestSchema,
} from './client-application';
// SEP-263 slice 8 - Review Client Application. Part 11 is not in this shape,
// and the duplicate flags of decision D10 travel with it.
export type {
    StaffApplicationAnswer,
    StaffApplicationSection,
    StaffApplicationKeyFact,
    StaffApplicationVerification,
    StaffIdentityCheckState,
    StaffApplicationTimeline,
    StaffApplicationIsaDeclaration,
    StaffApplicationParty,
    StaffClientApplication,
    StaffClientApplicationResponse,
} from './client-application-staff';
export {
    staffApplicationAnswerSchema,
    staffApplicationSectionSchema,
    staffApplicationKeyFactSchema,
    staffApplicationVerificationSchema,
    staffIdentityCheckStateSchema,
    staffIdentityCheckState,
    staffApplicationTimelineSchema,
    staffApplicationIsaDeclarationSchema,
    staffApplicationPartySchema,
    clientApplicationIsaDeclarationPdfPath,
    staffClientApplicationSchema,
    staffClientApplicationResponseSchema,
} from './client-application-staff';
export type {
    GovernanceMeetingKind,
    GovernanceRag,
    GovernanceStage,
    GovernanceItemStatus,
    GovernanceDecisionOutcome,
    GovernanceItemAuditEntry,
    GovernanceProgrammeItem,
    GovernanceDecision,
    GovernanceMeeting,
    GovernanceAgendaLine,
    GovernancePaper,
    StaleAmberFlag,
    GovernanceDriftResponse,
    GovernanceItemsResponse,
    GovernanceItemResponse,
    GovernanceMeetingsResponse,
    GovernanceMeetingDetailResponse,
    GovernancePapersResponse,
    StatusUpdateRequest,
} from './governance';
export type {
    VulnerabilitySeverity,
    VulnerabilityFlagDto,
    VulnerabilityEvidenceDto,
    VulnerabilityAdaptationPlanDto,
    VulnerabilityRecordDto,
    VulnerabilityDriverPrevalence,
    VulnerabilityDashboard,
    VulnerabilityCaseStudy,
    ConfirmFlagRequest,
    CreateFlagRequest,
    ConsentUpdateRequest,
} from './vulnerability';
export type {
    HouseModelAllocation,
    HouseModel,
    SecurityListEntry,
    PcfDeviationDetail,
    ImDeviationCell,
    PcfDashboard,
    BuyListImpactLine,
    BuyListChange,
    JustifyDeviationRequest,
    BuyListChangeRequest,
} from './pcf';
export type {
    ReconciliationBreakStatus,
    ReconciliationBreakDto,
    BreakResolutionEntry,
    ReconciliationKind,
    ResolveBreakRequest,
    AssignBreakRequest,
    AutoResolveBreaksRequest,
    AutoResolveBreaksResponse,
    BreakResponse as ReconciliationBreakResponse,
    ListBreaksResponse,
    ReconciliationResultDto,
    MasonClassificationDto,
    MasonRejectionDto,
    ListResultsResponse,
} from './reconciliation';
export type {
    OdCashRowDto,
    OverdrawnCashResponse,
    UploadOverdrawnCashResponse,
    AssignOverdraftOwnerRequest,
    ResolveOverdraftRowRequest,
    EscalateOverdraftRowRequest,
    OverdraftActionResponse,
} from './cash';
export type { OpsTradeDto, OpsTradeStatus, ListTradesResponse } from './ops';
export { opsTradeDtoSchema, opsTradeStatusSchema, listTradesResponseSchema } from './ops';
export type {
    CashPositionRowDto,
    CashPositionAlertDto,
    CashPositionsResponse,
    UploadCashAccountsResponse,
} from './cash';
export type {
    ManagerAliasSource,
    ManagerAliasRow,
    ListManagerAliasesResponse,
    CreateManagerAliasRequest,
    CreateManagerAliasResponse,
    ReattributionSummary,
    ReattributionUnresolved,
} from './manager-alias';
export type {
    BookHealthResponse,
    BookHealthDeskUser,
    BookHealthUnresolvedManager,
    BookHealthStaleAttribution,
    BookHealthManualAssignment,
} from '../schemas/book-health';
export type {
    BookHealthExceptionType,
    BookHealthExceptionStatus,
    BookHealthExceptionTargetKind,
    BookHealthExceptionLabel,
    BookHealthException,
    ListBookHealthExceptionsQuery,
    ListBookHealthExceptionsResponse,
    BookHealthExceptionDecision,
    MergeAccountsRequest,
    MergeAccountsResponse,
} from '../schemas/book-health-exceptions';
export {
    bookHealthExceptionTypeSchema,
    bookHealthExceptionStatusSchema,
    bookHealthExceptionTargetKindSchema,
    bookHealthExceptionLabelSchema,
    bookHealthExceptionSchema,
    bookHealthExceptionDecisionSchema,
} from '../schemas/book-health-exceptions';
export type {
    ClientBookIm,
    ClientBookHolder,
    ClientBookClient,
    ListClientBookImsResponse,
    ListClientBookClientsResponse,
    AssignClientBookRequest,
    ClearClientBookRequest,
    TransferClientBookRequest,
    ClientBookMutationResponse,
} from './client-books';
export type {
    McpConnectionStatus,
    McpConnectionDto,
    McpClientDto,
    McpConnectionsResponse,
    McpClientsResponse,
    McpRevokeAccessRequest,
    McpRemoveClientRequest,
    McpAdminMutationResponse,
} from '../schemas/mcp-admin';
export {
    mcpConnectionStatusSchema,
    mcpConnectionDtoSchema,
    mcpClientDtoSchema,
    mcpConnectionsResponseSchema,
    mcpClientsResponseSchema,
    mcpRevokeAccessRequestSchema,
    mcpRemoveClientRequestSchema,
    mcpAdminMutationResponseSchema,
} from '../schemas/mcp-admin';
export type {
    FirmConfigDto,
    TolerancesDto,
    TolerancesPatchDto,
    AddUserRequest,
    UpdateUserRequest,
    SeatKeyDto,
    SeatClassDto,
    SeatOverviewDto,
} from './firm-config';
export {
    firmConfigSchema,
    tolerancesSchema,
    tolerancesPatchSchema,
    addUserRequestSchema,
    seatKeySchema,
    seatClassSchema,
    seatOverviewSchema,
} from './firm-config';
export type { SystemStatusComponentDto, SystemStatusResponse } from './system-status';
export { systemStatusComponentSchema, systemStatusResponseSchema } from './system-status';
export type {
    RetentionCategoryDto,
    RetentionScheduleEntryDto,
    LegalHoldDto,
    RetentionCategoryReportDto,
    RetentionSweepReportDto,
    RetentionOverviewDto,
    SetRetentionCategoryDto,
    CreateLegalHoldDto,
    ReleaseLegalHoldDto,
} from './retention';
export {
    retentionContract,
    retentionCategorySchema,
    retentionScheduleEntrySchema,
    legalHoldSchema,
    retentionCategoryReportSchema,
    retentionSweepReportSchema,
    retentionOverviewSchema,
    setRetentionCategorySchema,
    createLegalHoldSchema,
    releaseLegalHoldSchema,
} from './retention';
export type {
    KpiAggregateDto,
    KpiManagerAggregateDto,
    KpiPerCategoryDto,
    KpiPerManagerDto,
    KpiTrendPointDto,
    KpiFirmTotalsDto,
    KpiAumAuaDto,
    KpiFirmResponse,
    KpiMyResponse,
    ReportingCategory,
} from './kpi';
export {
    REPORTING_CATEGORIES,
    kpiAggregateSchema,
    kpiManagerAggregateSchema,
    kpiTrendPointSchema,
    kpiFirmResponseSchema,
    kpiMyResponseSchema,
} from './kpi';
// KPI Reporting platform — the upload-driven monthly report. Separate from the
// live `kpi` surface above; see the header of ./kpi-reporting.ts.
export {
    KPI_CLIENT_CATEGORIES,
    KPI_FILE_TYPES,
    KPI_METRIC_KEYS,
    KPI_EXCEPTION_ISSUES,
    kpiClientCategorySchema,
    kpiFileTypeSchema,
    kpiMetricKeySchema,
    kpiMetricsSchema,
    kpiClientRowSchema,
    kpiManagerRowSchema,
    kpiCategoryRowSchema,
    kpiDashboardResponseSchema,
    kpiReportingTrendPointSchema,
    kpiTrendResponseSchema,
    kpiReportingMyResponseSchema,
    kpiReportManagerRowSchema,
    kpiReportSectionSchema,
    kpiReportResponseSchema,
    kpiFilterOptionsResponseSchema,
    kpiMonthStatusSchema,
    kpiMonthsResponseSchema,
    kpiExceptionRowSchema,
    kpiExceptionsResponseSchema,
    resolveKpiExceptionsBodySchema,
    resolveKpiExceptionsResponseSchema,
    kpiUploadResultSchema,
} from './kpi-reporting';
export type {
    KpiClientCategory,
    KpiFileType,
    KpiMetricKey,
    KpiExceptionIssue,
    KpiMetricsDto,
    KpiClientRowDto,
    KpiManagerRowDto,
    KpiCategoryRowDto,
    KpiDashboardResponse,
    KpiReportingTrendPointDto,
    KpiTrendResponse,
    KpiReportingMyResponse,
    KpiReportManagerRowDto,
    KpiReportSectionDto,
    KpiReportResponse,
    KpiFilterOptionsResponse,
    KpiMonthFileDto,
    KpiMonthStatusDto,
    KpiMonthsResponse,
    KpiExceptionRowDto,
    KpiExceptionsResponse,
    ResolveKpiExceptionsBody,
    ResolveKpiExceptionsResponse,
    KpiUploadResult,
} from './kpi-reporting';
// Benchmarking module (SEP-199) — client performance against the ARC peer
// benchmark. See the header of ./peer-benchmarking.ts.
export {
    PEER_BENCHMARK_PERIODS,
    PEER_BENCHMARK_PERIOD_LABELS,
    PEER_BENCHMARK_BOOKS,
    PEER_BENCHMARK_CATEGORY_GROUPS,
    peerBenchmarkCategoryGroupSchema,
    peerBenchmarkPeriodSchema,
    peerBenchmarkBookSchema,
    peerBenchmarkMonthSchema,
    peerBenchmarkPeriodValuesSchema,
    peerBenchmarkRowSchema,
    peerBenchmarkMonthOptionSchema,
    peerBenchmarkingResponseSchema,
    peerBenchmarkUploadResponseSchema,
    peerIndexUploadResponseSchema,
} from './peer-benchmarking';
export type {
    PeerBenchmarkPeriod,
    PeerBenchmarkBook,
    PeerBenchmarkCategoryGroup,
    PeerBenchmarkPeriodValues,
    PeerBenchmarkRow,
    PeerBenchmarkMonthOption,
    PeerBenchmarkingResponse,
    PeerBenchmarkUploadResponse,
    PeerIndexUploadResponse,
} from './peer-benchmarking';
export type {
    ReportCategory,
    ReportFrequency,
    ReportFormat,
    ReportDefinitionDto,
    ReportCatalogueResponse,
} from './reporting';
export {
    reportCategorySchema,
    reportFrequencySchema,
    reportFormatSchema,
    reportDefinitionSchema,
    reportCatalogueResponseSchema,
} from './reporting';
export type {
    SeverityFilter,
    NotificationPrefsDto,
    NotificationPrefsPatchDto,
    Theme,
    Density,
    DisplayPrefsDto,
    DisplayPrefsPatchDto,
} from './user-prefs';
export {
    severityFilterSchema,
    notificationPrefsSchema,
    notificationPrefsPatchSchema,
    themeSchema,
    densitySchema,
    displayPrefsSchema,
    displayPrefsPatchSchema,
} from './user-prefs';
export type { DocumentDraftDto } from './document-drafts';
export { documentDraftSchema } from './document-drafts';
// SEP-243 section 9, decision D5 - the postcode lookup proxy.
export type {
    AddressLookupAvailability,
    AddressLookupResponse,
    AddressLookupResult,
} from './address-lookup';
export {
    addressLookupAvailabilitySchema,
    addressLookupResponseSchema,
    addressLookupResultSchema,
} from './address-lookup';

// SEP-243 section 39 - the consistency rules engine. The rule book, the raised
// flag and the two gates.
export type {
    ConsistencyThresholdDto,
    ConsistencyRuleRowDto,
    ConsistencyRuleSetResponse,
    ConsistencyFlagDto,
    ConsistencyFlagsResponse,
} from './consistency';
export {
    consistencyTierSchema,
    consistencyThresholdSchema,
    consistencyRuleRowSchema,
    consistencyRuleSetResponseSchema,
    publishConsistencyRuleSetRequestSchema,
    setConsistencyRuleRequestSchema,
    consistencyFlagSchema,
    consistencyFlagsResponseSchema,
    evaluateConsistencyResponseSchema,
    confirmConsistencyFlagRequestSchema,
    resolveConsistencyFlagRequestSchema,
} from './consistency';

// SEP-243 section 37 - the binary mandate download path. The browser opens it
// directly, because ts-rest carries JSON alone.
export {
    complianceConditionEvidencePath,
    complianceConditionResolvePath,
    mandateDownloadPath,
    signedDocumentDownloadPath,
    signedDocumentUploadPath,
} from './final-approval';

// SEP-288 - the suitability refresh of an existing client.
export {
    suitabilityRefreshSchema,
    saveRefreshChecklistRequestSchema,
    refreshDecisionRequestSchema,
} from './suitability-refresh';
export type {
    SuitabilityRefreshDto,
    SaveRefreshChecklistRequest,
    RefreshDecisionRequest,
} from './suitability-refresh';
export { clarificationQuestionSchema, clarificationRequestSchema } from './fact-find-clarification';
export type { ClarificationRequestDto } from './fact-find-clarification';
export {
    suitabilityFlagDtoSchema,
    suitabilityFlagsResponseSchema,
    complianceFlagRowSchema,
    suitabilityFlagSeveritySchema,
    suitabilityFlagStatusSchema,
} from './suitability-flags';
export type {
    SuitabilityFlagDto,
    SuitabilityFlagsResponse,
    ComplianceFlagRow,
} from './suitability-flags';

// The book value history of the Desk Dashboard (SEP-297).
export {
    BOOK_HISTORY_RANGES,
    BOOK_HISTORY_SOURCES,
    bookHistoryRangeSchema,
    bookHistoryGranularitySchema,
    bookHistorySourceSchema,
    bookHistoryFilterQuerySchema,
    bookHistoryPointSchema,
    bookHistoryResponseSchema,
    bookHistoryDriversSchema,
    bookHistoryClientRowSchema,
    bookHistoryPointDetailSchema,
} from './book-history';
export type {
    BookHistoryRange,
    BookHistoryGranularity,
    BookHistorySource,
    BookHistoryFilterQuery,
    BookHistoryPoint,
    BookHistoryResponse,
    BookHistoryDrivers,
    BookHistoryClientRow,
    BookHistoryPointDetail,
} from './book-history';
